/**
 * RIG-mode skin weight display: the selected joint's influence as a heat ramp, plus the leak
 * overlay from the last test-pose check, plus the raycast that turns a click into a vertex.
 *
 * There is no brush any more. Weights are corrected by moving joints / editing per-joint
 * parameters (which re-solves) or by reassigning a whole region to another bone — this layer
 * only *shows* the result and reports where the user clicked.
 *
 * The display never touches the exportable geometry. It renders a *second* SkinnedMesh that
 * shares the original's position/normal/skin attributes by reference (no copy, no extra memory of
 * consequence) and adds only its own `color` attribute. Writing vertex colours onto the real
 * geometry would be simpler and would also bake a debug visualisation into every GLB the user
 * exports. Sharing by reference is also what makes a live re-solve cheap: the controller writes
 * into the same attribute arrays, and this layer only has to recolour on `'skin-changed'`.
 *
 * That sharing is re-validated on every `'skin-changed'` (`invalidate` → `reshareAttributes`),
 * because a re-solve that *replaces* an attribute instead of writing into it would otherwise leave
 * the overlay posing with generation-time weights — the correction would be invisible on exactly
 * the mesh the user is watching.
 *
 * Single-mesh: everything here works on `skinnedMesh(0)`.
 */

import {
  BufferAttribute,
  BufferGeometry,
  Group,
  MeshBasicMaterial,
  Raycaster,
  SkinnedMesh,
  Vector3,
} from 'three';
import type { Camera, Vector2 } from 'three';

import type { Vec3Tuple } from '../../shared';
import type { EditorContext } from '../context';
import type { LeakSummary } from '../types';
import { boneIndexOfJoint } from './gizmos';
import { flatColors, heatColorsForMask } from './heat';

const SHARED_ATTRIBUTES = ['position', 'normal', 'skinIndex', 'skinWeight'] as const;

/** Flat `#ff2020` for vertices the last leak check flagged. Nothing else in the overlay is red. */
const LEAK_COLOR: readonly [number, number, number] = [1, 0x20 / 0xff, 0x20 / 0xff];

/** A leak mask entry above this counts as leaked (the mask is 0/1 but typed as floats). */
const LEAK_EPS = 0.5;

export interface SkinPick {
  /** World-space hit point. */
  point: Vec3Tuple;
  /** Triangle index in the geometry, or −1 when three did not report one. */
  faceIndex: number;
  /** The hit triangle's vertex nearest the hit point — the seed for a region reassignment. */
  vertexIndex: number;
}

export interface SkinHeatLayer {
  update(): void;
  /** Viewport display switch (`Weights` in the overlay bar). Does not touch editor state. */
  setVisible(visible: boolean): void;
  /** Raycast the live skinned mesh (posed, if a test pose is previewing). */
  pick(ndc: Vector2, camera: Camera): SkinPick | null;
  /** Drop cached colours so the next `update` re-reads the masks. */
  invalidate(): void;
  dispose(): void;
}

export function createSkinHeatLayer(overlayGroup: Group, ctx: EditorContext): SkinHeatLayer {
  const group = new Group();
  group.name = 'asx-skinheat';
  group.userData.editorOnly = true;
  group.visible = false;
  overlayGroup.add(group);

  /** Display switch from the viewport bar; off means the plain mesh stays on screen. */
  let shown = true;

  const material = new MeshBasicMaterial({ vertexColors: true });

  let heatMesh: SkinnedMesh | null = null;
  let heatGeometry: BufferGeometry | null = null;
  let colors: Float32Array | null = null;
  let sourceMesh: SkinnedMesh | null = null;
  let shownBoneIndex = Number.NaN;
  /** The report the current colours were painted for; `undefined` = never painted. */
  let shownLeak: LeakSummary | null | undefined = undefined;
  let dirtyColors = true;
  /** A re-solve happened: re-share the attributes and re-upload them, once, on the next update. */
  let dirtySkin = true;

  // --- heat mesh ----------------------------------------------------------

  function releaseHeatMesh(): void {
    if (heatMesh) group.remove(heatMesh);
    heatGeometry?.dispose();
    heatMesh = null;
    heatGeometry = null;
    colors = null;
    if (sourceMesh) sourceMesh.visible = true;
    sourceMesh = null;
  }

  function buildHeatMesh(mesh: SkinnedMesh): boolean {
    const src = mesh.geometry;
    const position = src.getAttribute('position');
    if (!position) return false;

    const geometry = new BufferGeometry();
    for (const name of SHARED_ATTRIBUTES) {
      const attr = src.getAttribute(name);
      if (attr) geometry.setAttribute(name, attr); // shared by reference — no copy
    }
    if (src.index) geometry.setIndex(src.index);

    colors = new Float32Array(position.count * 3);
    geometry.setAttribute('color', new BufferAttribute(colors, 3));

    const heat = new SkinnedMesh(geometry, material);
    heat.name = 'asx-skinheat-mesh';
    heat.userData.editorOnly = true;
    heat.frustumCulled = false;
    if (mesh.skeleton) heat.bind(mesh.skeleton, mesh.bindMatrix);
    heat.matrixAutoUpdate = false;

    heatGeometry = geometry;
    heatMesh = heat;
    group.add(heat);
    return true;
  }

  /**
   * Re-point the overlay at the source mesh's *current* attribute objects.
   *
   * Sharing by reference is only free while the reference stays valid. A re-solve that replaces
   * `skinIndex`/`skinWeight` (rather than writing into them) leaves the overlay skinning with
   * generation-time weights, so live correction, fix-leaks and region reassignment would move the
   * real mesh and not the thing the user is actually looking at. Cheap to check — four identity
   * comparisons — and it makes the overlay correct whichever way the controller writes.
   *
   * Returns false when the geometry has to be rebuilt from scratch (the vertex count moved, so
   * the colour buffer is the wrong size).
   */
  function reshareAttributes(mesh: SkinnedMesh): boolean {
    if (!heatGeometry || !colors) return false;
    const src = mesh.geometry;

    const position = src.getAttribute('position');
    if (!position || position.count * 3 !== colors.length) return false;

    for (const name of SHARED_ATTRIBUTES) {
      const attr = src.getAttribute(name);
      const own = heatGeometry.getAttribute(name);
      if (attr === own) continue;
      if (attr) heatGeometry.setAttribute(name, attr);
      else if (own) heatGeometry.deleteAttribute(name);
    }
    if (src.index !== heatGeometry.index) heatGeometry.setIndex(src.index);

    // The weights themselves changed even when the objects did not (the in-place write): flag
    // them so the GPU actually re-uploads. Setting it on the shared attribute covers both meshes.
    for (const name of ['skinIndex', 'skinWeight'] as const) {
      const attr = src.getAttribute(name);
      if (attr) attr.needsUpdate = true;
    }
    return true;
  }

  function refreshColors(boneIndex: number, leakSummary: LeakSummary | null): void {
    if (!colors || !heatGeometry) return;
    const vertexCount = colors.length / 3;

    const mask = boneIndex >= 0 ? ctx.rigTools.influenceMask(boneIndex) : null;
    if (mask && mask.length === vertexCount) heatColorsForMask(mask, colors);
    else flatColors(vertexCount, 0.35, colors);

    // Leaks are shown only while a leak *report* stands: a stale mask from a previous check would
    // be worse than no overlay at all.
    const leak = leakSummary ? ctx.rigTools.leakMask(0) : null;
    if (leak && leak.length === vertexCount) {
      for (let i = 0; i < vertexCount; i++) {
        if (leak[i] <= LEAK_EPS) continue;
        colors[i * 3] = LEAK_COLOR[0];
        colors[i * 3 + 1] = LEAK_COLOR[1];
        colors[i * 3 + 2] = LEAK_COLOR[2];
      }
    }

    const attr = heatGeometry.getAttribute('color');
    if (attr) attr.needsUpdate = true;
    shownBoneIndex = boneIndex;
    shownLeak = leakSummary;
    dirtyColors = false;
  }

  // --- per-frame ----------------------------------------------------------

  function active(): boolean {
    if (!shown) return false;
    const state = ctx.store.getState();
    return state.mode === 'rig' && state.rig.skinGenerated;
  }

  function update(): void {
    const on = active();
    group.visible = on;
    if (!on) {
      releaseHeatMesh();
      return;
    }

    // Mesh 0 only — see the note on `pick`. A model with several skinned meshes shows the heat
    // overlay on the first one and the plain mesh for the rest; nothing here throws on them.
    const mesh = ctx.rigTools.skinnedMesh(0);
    if (!mesh) {
      // No skinned mesh yet: leave the plain mesh from `modelLayer` on screen untouched.
      releaseHeatMesh();
      return;
    }

    if (mesh !== sourceMesh) {
      releaseHeatMesh();
      sourceMesh = mesh;
      if (!buildHeatMesh(mesh)) {
        sourceMesh = null;
        return;
      }
      dirtyColors = true;
      dirtySkin = false;
    } else if (dirtySkin && !reshareAttributes(mesh)) {
      // Vertex count moved under us: the whole overlay has to be rebuilt.
      releaseHeatMesh();
      sourceMesh = mesh;
      if (!buildHeatMesh(mesh)) {
        sourceMesh = null;
        return;
      }
      dirtyColors = true;
    }
    dirtySkin = false;
    if (!heatMesh) return;

    mesh.updateWorldMatrix(true, false);
    heatMesh.matrix.copy(mesh.matrixWorld);
    heatMesh.matrixWorldNeedsUpdate = true;
    mesh.visible = false;

    const state = ctx.store.getState();
    const boneIndex = boneIndexOfJoint(state.rig.selectedJoint);
    const leakSummary = state.rig.leakSummary;
    if (dirtyColors || boneIndex !== shownBoneIndex || leakSummary !== shownLeak) {
      refreshColors(boneIndex, leakSummary);
    }
  }

  function invalidate(): void {
    dirtyColors = true;
    dirtySkin = true;
  }

  // --- pointer ------------------------------------------------------------

  const raycaster = new Raycaster();
  const _vertex = new Vector3();

  /**
   * The hit triangle's vertex nearest the hit point.
   *
   * `hit.face` already resolves the index buffer (or its absence), and `getVertexPosition` on a
   * SkinnedMesh applies the bone transform — so this is the *posed* vertex, matching the posed
   * hit point three just computed.
   */
  function nearestVertexOfFace(
    mesh: SkinnedMesh,
    face: { a: number; b: number; c: number },
    point: Vector3,
  ): number {
    let best = -1;
    let bestDistance = Infinity;
    for (const index of [face.a, face.b, face.c]) {
      mesh.getVertexPosition(index, _vertex);
      _vertex.applyMatrix4(mesh.matrixWorld);
      const distance = _vertex.distanceToSquared(point);
      if (distance < bestDistance) {
        bestDistance = distance;
        best = index;
      }
    }
    return best;
  }

  function pick(ndc: Vector2, camera: Camera): SkinPick | null {
    // Mesh 0 only: a click on a second skinned mesh finds nothing and returns null rather than
    // reassigning the wrong mesh's vertices. Multi-mesh picking is not in this wave.
    const mesh = ctx.rigTools.skinnedMesh(0);
    if (!mesh) return null;

    mesh.updateWorldMatrix(true, false);
    // `SkinnedMesh.raycast` skins every candidate vertex (r185 `getVertexPosition` →
    // `applyBoneTransform`), so clicking works on the test-pose preview as well as on rest. Its
    // *bounds*, though, are cached from whatever pose they were computed in: recompute them here
    // or a posed limb outside the rest-pose sphere silently rejects the ray.
    mesh.computeBoundingSphere();
    if (mesh.boundingBox) mesh.computeBoundingBox();

    raycaster.setFromCamera(ndc, camera);
    const hit = raycaster.intersectObject(mesh, false)[0];
    if (!hit || !hit.face) return null;

    return {
      point: [hit.point.x, hit.point.y, hit.point.z],
      faceIndex: hit.faceIndex ?? -1,
      vertexIndex: nearestVertexOfFace(mesh, hit.face, hit.point),
    };
  }

  function dispose(): void {
    releaseHeatMesh();
    overlayGroup.remove(group);
    material.dispose();
  }

  return {
    update,
    setVisible(visible) {
      shown = visible;
    },
    pick,
    invalidate,
    dispose,
  };
}
